import * as React from "react";
import { HESPlink } from "../../Landing/SignUpComplete";
import styled from "styled-components";
import CodeSample from "../../CodeSample/CodeSample";

interface Props {
    channelId: string;
    license: string;
}

const P = styled.p`
    font-size: 15px;
`;

const Wrapper = styled.div`
    margin: 15px 10px;
`;

export const Code = styled.code`
    color: #de5b5b;
    padding: 2px;
    background-color: ${(props) => props.theme.grayBackground};
`;

function ChannelPlayoutIncludeDocumentation({ channelId, license }: Props) {
    return (
        <Wrapper>
            <P>
                Below, a full code sample is given on how to integrate THEOplayer with your THEOlive channel on your web
                page. We explain the main parts here.{" "}
            </P>
            <P>
                If you are looking for a more detailed guide, please refer to our{" "}
                <HESPlink
                    link="https://www.theoplayer.com/docs/theoplayer/how-to-guides/web/theolive/getting-started/"
                    openInNewTab
                >
                    documentation guide
                </HESPlink>
                .
            </P>

            <div className="font-semibold text-md mt-2">Steps to follow</div>
            <div className="mb-4 flex flex-col gap-2">
                <P>
                    1. Install the THEOplayer web SDK. This can be done using npm: <Code>npm install theoplayer</Code>
                </P>
                <P>
                    2. Include the stylesheet (<Code>theoplayer/ui.css</Code>) and script (<Code>THEOplayer.js</Code>).
                    In the example below, replace <Code>{"<PATH_TO_NODE_MODULES>"}</Code> to the location your{" "}
                    <Code>{"node_modules"}</Code> directory is located.
                </P>
                <P>
                    {`3. Create a video player container somewhere on your page: `}{" "}
                    <Code>{`<div class="theoplayer-container video-js theoplayer-skin vjs-16-9"></div>`}</Code>
                </P>
                <P>
                    {
                        "4. If you're planning to support a version lower than iOS v17.1, a service worker needs to be registered."
                    }
                </P>
                <P>5. Create a THEOplayer instance and pass your license</P>
                <P>6. Set your THEOlive source, with the right channel/alias ID</P>
            </div>
            <CodeSample
                language="html"
                code={`
<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>THEOplayer</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <!-- Include CSS stylesheet -->
    <link rel="stylesheet" type="text/css" href="<PATH_TO_NODE_MODULES>/theoplayer/ui.css" />
  </head>
  <body>
    <!-- Create video player container element-->
    <div class="theoplayer-container video-js theoplayer-skin vjs-16-9"></div>

    <script src="<PATH_TO_NODE_MODULES>/theoplayer/THEOplayer.js"></script>
    <script>

    // On Apple devices running an iOS version lower than 17.1, a service worker needs
    // to be registered to support low latency play-out of THEOlive streams. 
    function needsServiceWorkerForTHEOlive() {
        const canPlayMSE = !!(window.MediaSource || window.WebKitMediaSource || window.ManagedMediaSource);
        const videoElement = document.createElement("video");
        const canPlayHLS = videoElement.canPlayType && videoElement.canPlayType("application/vnd.apple.mpegURL") !== "";
        return canPlayHLS && !canPlayMSE;
    }
    
    if (needsServiceWorkerForTHEOlive()) {
        navigator.serviceWorker.register("<PATH_TO_NODE_MODULES>/theoplayer/theoplayer.sw.js");
    }

    // Retrieve THEOplayer container div
    var element = document.querySelector(".theoplayer-container");
    
    // create THEOplayer instance associated with the container
    var player = new THEOplayer.Player(element, {
        libraryLocation: "<PATH_TO_NODE_MODULES>/theoplayer",
        license: "${license}",
    });

    // set your THEOlive source
    player.source = {
        sources: {
            src: "${channelId}", // your channel/alias id
            type: "theolive",
        },        
    };
    </script>
  </body>
</html>`}
            ></CodeSample>
        </Wrapper>
    );
}

export default ChannelPlayoutIncludeDocumentation;
